import { EntityLabel, FileEntityLabelProps } from "./EntityLabel";
import { VersionPopover } from "./VersionPopover";
import { getFileTypeFromVersionId } from "../utils/versions";
import { File } from "@/types/app/file";
import { refetchFiles, useFile } from "@/services/files.service";
import { HoverPopover } from "./shared/HoverPopover";
import * as RadixPopover from "@radix-ui/react-popover";
import { useContext, useState } from "react";
import { VersionDrawerContext } from "@components/llama/VersionDrawer/useVersionDrawer";

// You can pass in a full Version object (preferably, saves a fetch) or
// IDs and this component will fetch the Version object for you.
type VersionIdentifierProps =
  | {
      file: File;
    }
  | {
      fileId: string;
      versionId: string;
      versionName?: string;
      versionDescription?: string;
    };

type FileLabelWithPopoverProps = (Omit<
  FileEntityLabelProps,
  "fileId" | "versionId" | "entity" | "deployedEnvironments" | "fileName" // To control whether `fileName` is displayed, use `displayFile`
> & { environment?: string | null }) &
  VersionIdentifierProps & {
    tooltipContentProps?: React.ComponentProps<typeof RadixPopover.Content>;
    target?: "_blank" | "_self";
    /** Generally don't use this as click should just show the popover - its only needed for dropdowns/filters maybe */
    onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
    displayFile?: boolean;
    side?: RadixPopover.PopoverContentProps["side"];
    align?: RadixPopover.PopoverContentProps["align"];
    openDelay?: number;
    // Whether the version name/description can be edited in the popover. To prevent editing in ConfirmDeploymentDialogPage
    versionPopoverProps?: {
      /** Controls whether version name and description can be edited */
      isEditable?: boolean;
      /** Controls visibility of the drawer open button */
      showOpenButton?: boolean;
      /** Callback to open the drawer */
      onOpenDrawer?: () => void;
    };
  };

/**
 * Label with an on-hover popover that displays Version details.
 *
 * This should only be used for File entities that have versions.
 */
export const FileLabelWithPopover = ({
  tooltipContentProps,
  size,
  target = "_blank",
  onClick,
  side,
  align,
  openDelay,
  ...props
}: FileLabelWithPopoverProps) => {
  // As this component allows the user to pass in a full File object or just a versionId,
  // we conditionally fetch within useFile only if versionId was provided (and not the full File object).
  const { file: fetchedFile, mutate } = useFile(
    "versionId" in props ? { fileId: props.fileId, versionId: props.versionId } : null,
  );
  const versionDrawerContext = useContext(VersionDrawerContext);
  const file = "file" in props ? props.file : fetchedFile; // Use passed in File object or fetched File (from ID) based on props.
  const versionId = "versionId" in props ? props.versionId : props.file.version_id;
  const fetchedFileName = fetchedFile?.name;

  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const [isEditing, setIsEditing] = useState(false);
  const [hasError, setHasError] = useState(false);

  // Callbacks for child component to communicate state changes
  const handleEditingStart = () => {
    setIsEditing(true);
  };

  const handleEditingEnd = () => {
    setIsEditing(false);
  };

  const handleErrorOccurred = () => {
    setHasError(true);
  };

  const handleErrorResolved = () => {
    setHasError(false);
  };

  const handlePopoverOpenChange = (open: boolean) => {
    if (!open && (isEditing || hasError)) {
      return;
    }

    setIsPopoverOpen(open);
  };

  const trigger = (
    <EntityLabel
      entity={getFileTypeFromVersionId(versionId)}
      versionId={versionId}
      versionName={file?.version_name}
      size={size}
      deployedEnvironments={file?.environments || []}
      fileName={props.displayFile ? file?.name || fetchedFileName : undefined}
      {...props}
    />
  );

  // We re-fetch the file as we want to ensure the new version name is reflected throughout the app.
  const content = file && (
    <VersionPopover
      file={file}
      onFileChange={(updatedFile) => {
        // Mutate
        refetchFiles(updatedFile);

        // Also update drawer context if this file is the selected version
        if (
          versionDrawerContext &&
          versionDrawerContext.selectedVersion &&
          versionDrawerContext.selectedVersion.version_id === updatedFile.version_id
        ) {
          versionDrawerContext.setSelectedVersion(updatedFile);
        }
      }}
      isEditable={props.versionPopoverProps?.isEditable ?? true}
      showOpenButton={props.versionPopoverProps?.showOpenButton ?? true}
      onOpenDrawer={props.versionPopoverProps?.onOpenDrawer}
      onEditingStart={handleEditingStart}
      onEditingEnd={handleEditingEnd}
      onErrorOccurred={handleErrorOccurred}
      onErrorResolved={handleErrorResolved}
    />
  );

  return (
    <HoverPopover
      trigger={trigger}
      content={content}
      tooltipContentProps={tooltipContentProps}
      triggerOnClick={onClick}
      side={side}
      align={align}
      openDelay={openDelay}
      open={isPopoverOpen}
      onOpenChange={handlePopoverOpenChange}
    />
  );
};
